// Cálculo da Apresentação de fluxos (Dashboard → Apresentação, 05/11/2026).
// Só leitura e sem React: recebe o fluxo e os cards (formato /api/cards/resumo) e devolve os números
// de cada slide. Fica separado da tela para dar para conferir os números rodando este arquivo direto
// contra os dados reais (scripts_vps/apres_*.py fazem a mesma conta pelo banco).

export type TipoEtapa = 'andamento' | 'concluida' | 'negativa';

export interface CampoDef { id: string; nome: string; tipo: string; opcoes?: string[] }
export interface EtapaDef {
  id: string; nome: string; campos?: CampoDef[];
  tempoMaximoHoras?: number; slaIgnorarFimDeSemana?: boolean;
}
export interface FluxoDef { id: string; name?: string; nome?: string; etapas: EtapaDef[] }
export interface HistDef { etapaId: string; movedAt?: any; leftAt?: any }
export interface CardDef {
  id: string; titulo?: string; flowId?: string; etapaId: string; shortCode?: string;
  responsavelIndividualId?: string; createdAt?: any; dataCriacao?: any;
  campos?: Record<string, any>; etapaHistory?: HistDef[];
}

export type Papel = 'filial' | 'centro' | 'solicitante' | 'fornecedor' | 'responsavel' | 'categoria';
export interface Dimensao {
  id: string;        // id do campo ou '__responsavel'
  nome: string;
  papel: Papel;
  multi: boolean;    // checkbox: o card pode ter vários valores (valor dividido igual entre eles)
  valores: (c: CardDef) => string[];
}

export interface ConfigApres {
  inicio: Date | null;           // período (data de criação / de conclusão), inclusive
  fim: Date | null;              // exclusivo
  tipos: Record<string, TipoEtapa>;
  campoValor: string;            // 'auto' | id do campo | '' (sem valor)
  filtros: Record<string, string[]>;  // id da dimensão -> valores aceitos
  agora?: Date;
  periodoId?: string;            // '90' quebra em 3 ciclos de 30 dias; o resto, mês a mês
}

const DIA = 864e5;
const HORA = 36e5;

// ---------- leitura de valores ----------

/** Data de card/histórico: cards de portal têm `dataCriacao` em UTC sem fuso. */
export function lerData(v: any): Date | null {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'object' && v.$date) return lerData(v.$date);
  const t = String(v).trim();
  let d: Date;
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(t) && !/[zZ]|[+-]\d\d:?\d\d$/.test(t)) d = new Date(t.replace(' ', 'T') + 'Z');
  else d = new Date(t);
  return isNaN(d.getTime()) ? null : d;
}
export const criadoEm = (c: CardDef) => lerData(c.createdAt) || lerData(c.dataCriacao);

/** Lista de um campo de escolha. Alguns cards guardam a lista como texto ("['A', 'B']"). */
export function comoLista(v: any): string[] {
  if (v === null || v === undefined || v === '') return [];
  if (Array.isArray(v)) return v.map(x => String(x).trim()).filter(Boolean);
  const s = String(v).trim();
  if (s.startsWith('[') && s.endsWith(']')) {
    const miolo = s.slice(1, -1).trim();
    if (!miolo) return [];
    return miolo.split(',').map(x => x.trim().replace(/^['"]|['"]$/g, '').trim()).filter(Boolean);
  }
  return [s];
}

// Mesma regra do relatório "Gastos por filial": "1.234,56" / "1532.00" / "6.350" (milhar) / "R$ 99,90"
export function lerValorBR(v: any): number {
  if (v === undefined || v === null || v === '') return 0;
  if (typeof v === 'number') return isNaN(v) ? 0 : v;
  const s = String(v).replace(/R\$/gi, '').replace(/\s/g, '');
  if (!s) return 0;
  let n: number;
  if (s.includes(',')) n = Number(s.replace(/\./g, '').replace(',', '.'));
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) n = Number(s.replace(/\./g, ''));
  else n = Number(s);
  return isNaN(n) ? 0 : n;
}

// ---------- horas (SLA) ----------

/** Horas úteis seg–sex (dia inteiro), como o slaUtils do Dashboard. */
function horasUteis(a: Date, b: Date): number {
  if (b <= a) return 0;
  let total = 0;
  let cur = new Date(a.getTime());
  while (cur < b) {
    const fimDia = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
    const ate = fimDia < b ? fimDia : b;
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) total += (ate.getTime() - cur.getTime()) / HORA;
    cur = ate;
  }
  return total;
}
export type FnHoras = (a: Date, b: Date, ignorarFds?: boolean) => number;
const horasPadrao: FnHoras = (a, b, fds) => fds ? horasUteis(a, b) : Math.max(0, (b.getTime() - a.getTime()) / HORA);

// ---------- etapas ----------

const RX_NEGATIVA = /reprova|cancel|recusad|negad|n[aã]o aprovad/i;
const RX_CONCLUIDA = /conclu|finalizad|arquivo|encerrad|resolvid/i;

export interface StatEtapa { atual: number; passou: number }

/** Classificação automática: nome primeiro; sem nome que diga, etapa onde os cards entram e
 *  não saem mais (≥ 10 cards hoje e ≥ 90% dos que já passaram) conta como concluída. */
export function tiposAutomaticos(fluxo: FluxoDef, cards: CardDef[]): Record<string, TipoEtapa> {
  const st = statsEtapas(fluxo, cards);
  const out: Record<string, TipoEtapa> = {};
  fluxo.etapas.forEach(e => {
    const s = st[e.id] || { atual: 0, passou: 0 };
    if (RX_NEGATIVA.test(e.nome)) out[e.id] = 'negativa';
    else if (RX_CONCLUIDA.test(e.nome)) out[e.id] = 'concluida';
    else if (s.atual >= 10 && s.passou > 0 && s.atual / s.passou >= 0.9) out[e.id] = 'concluida';
    else out[e.id] = 'andamento';
  });
  // sem nenhuma concluída: a última etapa faz o papel
  if (!Object.values(out).includes('concluida') && fluxo.etapas.length) out[fluxo.etapas[fluxo.etapas.length - 1].id] = 'concluida';
  return out;
}

export function statsEtapas(fluxo: FluxoDef, cards: CardDef[]): Record<string, StatEtapa> {
  const out: Record<string, StatEtapa> = {};
  fluxo.etapas.forEach(e => { out[e.id] = { atual: 0, passou: 0 }; });
  cards.forEach(c => {
    if (out[c.etapaId]) out[c.etapaId].atual += 1;
    new Set((c.etapaHistory || []).map(h => String(h.etapaId))).forEach(id => { if (out[id]) out[id].passou += 1; });
  });
  return out;
}

// ---------- campos ----------

export function camposDoFluxo(fluxo: FluxoDef): { campo: CampoDef; etapaIdx: number }[] {
  const vistos = new Set<string>();
  const out: { campo: CampoDef; etapaIdx: number }[] = [];
  fluxo.etapas.forEach((e, i) => (e.campos || []).forEach(c => {
    if (!c || vistos.has(c.id)) return;
    vistos.add(c.id); out.push({ campo: c, etapaIdx: i });
  }));
  return out;
}

export const nomeLimpo = (s: string) => String(s || '').replace(/\s*\(.*?\)\s*/g, ' ').replace(/[:\s]+$/, '').replace(/\s+/g, ' ').trim();

/** Campos de valor do fluxo, na ordem que o modo automático usa (bruto → total → médio). Fica de fora
 *  o que não é o valor da solicitação: "Valor" das cotações, líquido, mensal, usado da ND, novo valor. */
export function camposValor(fluxo: FluxoDef): { todos: CampoDef[]; auto: CampoDef[] } {
  const todos = camposDoFluxo(fluxo).map(x => x.campo).filter(c => c.tipo === 'valor');
  const peso = (n: string) => /bruto/i.test(n) ? 0 : /total/i.test(n) ? 1 : /m[eé]dio/i.test(n) ? 2 : 3;
  const auto = todos
    .filter(c => !/^\s*valor\s*:?\s*$/i.test(c.nome) && !/l[ií]quido|mensal|usad|novo valor/i.test(c.nome))
    .sort((a, b) => peso(a.nome) - peso(b.nome));
  return { todos, auto };
}

export function leitorValor(fluxo: FluxoDef, escolha: string): { ler: (c: CardDef) => number; nome: string } | null {
  if (!escolha) return null;
  const { todos, auto } = camposValor(fluxo);
  const cadeia = escolha === 'auto' ? auto : todos.filter(c => c.id === escolha);
  if (!cadeia.length) return null;
  return {
    nome: cadeia.map(c => nomeLimpo(c.nome)).join(' → '),
    ler: (c: CardDef) => {
      for (const campo of cadeia) {
        const n = lerValorBR(c.campos?.[campo.id]);
        if (n) return n;
      }
      return 0;
    },
  };
}

/** Texto digitado em editor rico chega com tag e entidade ("<div>BUFFET", "Dell&nbsp;"). */
export const textoLimpo = (v: string) => String(v).replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&')
  .replace(/&lt;/gi, '<').replace(/&gt;/gi, '>').replace(/&quot;/gi, '"').replace(/&#39;/gi, "'").replace(/\s+/g, ' ').trim();

const TIPOS_ESCOLHA = new Set(['lista', 'checkbox', 'checkbox_single', 'checkbox_multi', 'responsavel']);
const RX_FORA = /e-?mail|aprova|valida|motivo|justific|conformidade|assinatura|nota fiscal|enviar|destino|pagamento|ativo|via e-?mail|aditivo|reajuste|despesas de gerentes|criticidade/i;

/** Dimensões que viram slide: filial, centro de custo, solicitante, fornecedor, responsável do card e
 *  até 3 campos de escolha do formulário de abertura (1ª etapa) bem preenchidos. */
export function detectarDimensoes(fluxo: FluxoDef, cards: CardDef[], nomeColab: Map<string, string>): Dimensao[] {
  const base = cards.length || 1;
  const campos = camposDoFluxo(fluxo);
  const out: Dimensao[] = [];
  const preench = (ler: (c: CardDef) => string[]) => {
    let n = 0; const dist = new Set<string>();
    cards.forEach(c => { const v = ler(c); if (v.length) { n++; v.forEach(x => dist.add(x.toLowerCase())); } });
    return { taxa: n / base, distintos: dist.size };
  };
  const usados = new Set<string>();
  const add = (campo: CampoDef, papel: Papel) => {
    if (usados.has(campo.id) || out.some(d => d.papel === papel && papel !== 'categoria')) return;
    const ler = (c: CardDef) => {
      const vals = comoLista(c.campos?.[campo.id]).map(textoLimpo).filter(Boolean);
      if (campo.tipo === 'responsavel') return vals.map(v => nomeColab.get(v) || v);
      if (papel === 'fornecedor' || papel === 'solicitante') return vals.slice(0, 1);
      return vals;
    };
    const p = preench(ler);
    if (p.taxa < 0.3 || p.distintos < 2) return;
    usados.add(campo.id);
    out.push({ id: campo.id, nome: nomeLimpo(campo.nome), papel, multi: campo.tipo === 'checkbox', valores: ler });
  };
  // papéis pelo nome (o campo pode estar em qualquer etapa para filial/centro; o resto, na 1ª)
  campos.forEach(({ campo }) => { if (TIPOS_ESCOLHA.has(campo.tipo) && /filia/i.test(campo.nome)) add(campo, 'filial'); });
  campos.forEach(({ campo }) => { if (TIPOS_ESCOLHA.has(campo.tipo) && /centro de custo|setor|departamento/i.test(campo.nome)) add(campo, 'centro'); });
  campos.forEach(({ campo, etapaIdx }) => {
    if (etapaIdx === 0 && (TIPOS_ESCOLHA.has(campo.tipo) || campo.tipo === 'texto') && /^\s*nome\s*:?\s*$|solicitante|requisitante|seu nome/i.test(campo.nome)) add(campo, 'solicitante');
  });
  campos.forEach(({ campo, etapaIdx }) => {
    if (etapaIdx === 0 && campo.tipo === 'texto' && /fornecedor/i.test(campo.nome) && !/\d/.test(campo.nome)) add(campo, 'fornecedor');
  });
  // responsável do card
  const lerResp = (c: CardDef) => {
    const r = c.responsavelIndividualId ? String(c.responsavelIndividualId) : '';
    return r ? [nomeColab.get(r) || r] : [];
  };
  const pr = preench(lerResp);
  if (pr.taxa >= 0.3 && pr.distintos >= 2) out.push({ id: '__responsavel', nome: 'Responsável do card', papel: 'responsavel', multi: false, valores: lerResp });
  // categorias: escolha da 1ª etapa, as mais preenchidas
  const cand = campos
    .filter(({ campo, etapaIdx }) => etapaIdx === 0 && TIPOS_ESCOLHA.has(campo.tipo) && !usados.has(campo.id) && !RX_FORA.test(campo.nome))
    .map(({ campo }) => ({ campo, p: preench(c => comoLista(c.campos?.[campo.id])) }))
    .filter(x => x.p.taxa >= 0.3 && x.p.distintos >= 2)
    .sort((a, b) => b.p.taxa - a.p.taxa)
    .slice(0, 3);
  cand.forEach(({ campo }) => add(campo, 'categoria'));
  return out;
}

// ---------- o cálculo ----------

export interface LinhaDim {
  valor: string; qtd: number; concluidos: number; abertos: number; valorTotal: number; cicloMedioDias: number | null;
}
export interface LinhaEtapa {
  id: string; nome: string; tipo: TipoEtapa; passaram: number; tempoMedioH: number | null; tempoMedianoH: number | null;
  abertosAgora: number; atrasadosAgora: number; slaH: number | null; visitas: number;
}
export interface Bucket { chave: string; rotulo: string; entradas: number; concluidos: number; valor: number }
export interface CardDestaque {
  id: string; shortCode?: string; titulo: string; etapa: string; valor: number; dias: number; extra: string;
}

export interface ResultadoApres {
  nomeFluxo: string;
  totalFluxo: number;           // cards do fluxo depois dos filtros (todo o período)
  entradas: number;             // criados no período
  concluidos: number;           // concluídos no período (pela data de conclusão)
  reprovados: number;           // em etapa negativa, entrada nela no período
  abertosHoje: number;
  atrasadosHoje: number;
  comSlaHoje: number;           // abertos em etapa com SLA
  cicloMedioDias: number | null;
  cicloMedianoDias: number | null;
  semDataConclusao: number;
  valorNome: string | null;
  valorEntradas: number;        // soma do valor dos criados no período, sem os reprovados
  valorConcluido: number;       // soma do valor dos concluídos no período
  ticketMedio: number | null;
  cardsComValor: number;
  etapas: LinhaEtapa[];
  evolucao: Bucket[];
  granularidade: 'mes' | 'semana';
  dims: { dim: Dimensao; linhas: LinhaDim[] }[];
  aging: { faixa: string; qtd: number }[];
  maioresValores: CardDestaque[];
  maisAntigos: CardDestaque[];
  periodoTexto: string;
  slaJanelas: string[];          // rótulos das colunas (mês ou ciclo de 30 dias)
  slaCiclos: boolean;
  slaMesParcial: string | null;  // mês corrente deixado fora das colunas (entra na média)
  slaMensal: LinhaSlaMensal[];
  fluxoMensal: { concluidos: number; cicloMedioDias: number | null; pctNoPrazo: number | null }[];
  fluxoTotal: { concluidos: number; cicloMedioDias: number | null; pctNoPrazo: number | null };
}
export interface CelulaSla { n: number; medioH: number | null; medianoH: number | null; pctSla: number | null }
export interface LinhaSlaMensal { id: string; nome: string; slaH: number | null; total: CelulaSla; meses: CelulaSla[] }

const fmtD = (d: Date) => d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
/** Ano-mês e dia no fuso de Brasília (a VPS grava em UTC). */
function partesBR(d: Date) {
  const s = d.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }); // aaaa-mm-dd
  return { a: Number(s.slice(0, 4)), m: Number(s.slice(5, 7)), dia: Number(s.slice(8, 10)), iso: s };
}
const media = (xs: number[]) => xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
const mediana = (xs: number[]) => {
  if (!xs.length) return null;
  const o = [...xs].sort((a, b) => a - b); const m = Math.floor(o.length / 2);
  return o.length % 2 ? o[m] : (o[m - 1] + o[m]) / 2;
};

/** Momento em que o card chegou ao estado final em que está: o começo da sequência final de
 *  passagens por etapas do mesmo tipo (Concluídos → Arquivo conta da entrada em Concluídos). */
export function dataEstadoFinal(c: CardDef, tipos: Record<string, TipoEtapa>): Date | null {
  const alvo = tipos[c.etapaId];
  const h = c.etapaHistory || [];
  let d: Date | null = null;
  for (let i = h.length - 1; i >= 0; i--) {
    if (tipos[String(h[i].etapaId)] !== alvo) break;
    const m = lerData(h[i].movedAt);
    if (m) d = m;
  }
  return d;
}

export function calcular(fluxo: FluxoDef, todosCards: CardDef[], cfg: ConfigApres, dims: Dimensao[], horas: FnHoras = horasPadrao): ResultadoApres {
  const agora = cfg.agora || new Date();
  const tipos = cfg.tipos;
  const etapaPorId = new Map(fluxo.etapas.map(e => [e.id, e]));
  const noPeriodo = (d: Date | null) => !!d && (!cfg.inicio || d >= cfg.inicio) && (!cfg.fim || d < cfg.fim);

  // cards do fluxo (por flowId: Compras e Obras têm as MESMAS ids de etapa) e numa etapa que existe
  let cards = todosCards.filter(c => (!c.flowId || String(c.flowId) === String(fluxo.id)) && etapaPorId.has(c.etapaId));
  Object.entries(cfg.filtros).forEach(([dimId, aceitos]) => {
    if (!aceitos?.length) return;
    const d = dims.find(x => x.id === dimId);
    if (!d) return;
    cards = cards.filter(c => d.valores(c).some(v => aceitos.includes(v)));
  });

  const valor = leitorValor(fluxo, cfg.campoValor);
  const lerV = (c: CardDef) => valor ? valor.ler(c) : 0;
  const tipoDe = (c: CardDef): TipoEtapa => tipos[c.etapaId] || 'andamento';

  const criados = cards.filter(c => noPeriodo(criadoEm(c)));
  const finais = new Map<string, Date | null>();
  cards.forEach(c => { if (tipoDe(c) !== 'andamento') finais.set(c.id, dataEstadoFinal(c, tipos)); });
  const concluidos = cards.filter(c => tipoDe(c) === 'concluida' && noPeriodo(finais.get(c.id) || null));
  const semDataConclusao = cards.filter(c => tipoDe(c) === 'concluida' && !finais.get(c.id)).length;
  const reprovados = cards.filter(c => tipoDe(c) === 'negativa' && noPeriodo(finais.get(c.id) || null));
  const abertos = cards.filter(c => tipoDe(c) === 'andamento');

  // ciclo: criação → conclusão
  const ciclo = new Map<string, number>();
  concluidos.forEach(c => {
    const a = criadoEm(c); const b = finais.get(c.id);
    if (a && b) ciclo.set(c.id, Math.max(0, (b.getTime() - a.getTime()) / DIA));
  });
  const ciclos = Array.from(ciclo.values());

  // atraso de SLA agora (mesma conta do Dashboard: última entrada na etapa atual até agora)
  const entradaAtual = (c: CardDef): Date | null => {
    const h = c.etapaHistory || [];
    for (let i = h.length - 1; i >= 0; i--) if (String(h[i].etapaId) === String(c.etapaId)) return lerData(h[i].movedAt);
    return null;
  };
  let comSlaHoje = 0;
  const atrasado = new Set<string>();
  abertos.forEach(c => {
    const e = etapaPorId.get(c.etapaId);
    if (!e?.tempoMaximoHoras) return;
    comSlaHoje++;
    const ent = entradaAtual(c);
    if (ent && horas(ent, agora, e.slaIgnorarFimDeSemana) > e.tempoMaximoHoras) atrasado.add(c.id);
  });

  // etapas: passagens que COMEÇARAM no período (de qualquer card do fluxo filtrado)
  const porEtapa = new Map<string, { cards: Set<string>; tempos: number[] }>();
  const visitas: { etapaId: string; ent: Date; horas: number; slaOk: boolean | null }[] = [];
  fluxo.etapas.forEach(e => porEtapa.set(e.id, { cards: new Set(), tempos: [] }));
  cards.forEach(c => {
    const h = c.etapaHistory || [];
    h.forEach((p, i) => {
      const id = String(p.etapaId);
      const r = porEtapa.get(id);
      const ent = lerData(p.movedAt);
      if (!r || !noPeriodo(ent)) return;
      r.cards.add(c.id);
      if ((tipos[id] || 'andamento') !== 'andamento') return;   // em etapa final o tempo não para de correr
      let saida = lerData(p.leftAt) || (i + 1 < h.length ? lerData(h[i + 1].movedAt) : null);
      if (!saida && i === h.length - 1 && String(c.etapaId) === id) saida = agora;
      if (ent && saida && saida >= ent) {
        const hh = (saida.getTime() - ent.getTime()) / HORA;
        r.tempos.push(hh);
        const e = etapaPorId.get(id);
        visitas.push({ etapaId: id, ent, horas: hh, slaOk: e?.tempoMaximoHoras ? horas(ent, saida, e.slaIgnorarFimDeSemana) <= e.tempoMaximoHoras : null });
      }
    });
  });
  const etapas: LinhaEtapa[] = fluxo.etapas.map(e => {
    const r = porEtapa.get(e.id)!;
    return {
      id: e.id, nome: e.nome, tipo: tipos[e.id] || 'andamento', passaram: r.cards.size, visitas: r.tempos.length,
      tempoMedioH: media(r.tempos), tempoMedianoH: mediana(r.tempos),
      abertosAgora: abertos.filter(c => c.etapaId === e.id).length,
      atrasadosAgora: abertos.filter(c => c.etapaId === e.id && atrasado.has(c.id)).length,
      slaH: e.tempoMaximoHoras || null,
    };
  });

  // evolução: mês a mês, ou semana a semana em período curto
  const datasCriacao = cards.map(criadoEm).filter((d): d is Date => !!d);
  const ini = cfg.inicio || (datasCriacao.length ? new Date(Math.min(...datasCriacao.map(d => d.getTime()))) : agora);
  const fim = cfg.fim && cfg.fim < agora ? cfg.fim : agora;
  const granularidade: 'mes' | 'semana' = (fim.getTime() - ini.getTime()) / DIA <= 62 ? 'semana' : 'mes';
  const chaveDe = (d: Date) => {
    const p = partesBR(d);
    if (granularidade === 'mes') return `${p.a}-${String(p.m).padStart(2, '0')}`;
    // semana começando na segunda
    const dd = new Date(Date.UTC(p.a, p.m - 1, p.dia));
    const dow = (dd.getUTCDay() + 6) % 7;
    dd.setUTCDate(dd.getUTCDate() - dow);
    return dd.toISOString().slice(0, 10);
  };
  const rotuloDe = (k: string) => granularidade === 'mes'
    ? `${MESES[Number(k.slice(5, 7)) - 1]}/${k.slice(2, 4)}`
    : `${k.slice(8, 10)}/${k.slice(5, 7)}`;
  const buckets = new Map<string, Bucket>();
  // todas as chaves do intervalo, mesmo as vazias
  {
    const passo = granularidade === 'mes' ? null : 7 * DIA;
    let cur = new Date(ini.getTime());
    let guard = 0;
    while (cur <= fim && guard++ < 400) {
      const k = chaveDe(cur);
      if (!buckets.has(k)) buckets.set(k, { chave: k, rotulo: rotuloDe(k), entradas: 0, concluidos: 0, valor: 0 });
      if (passo) cur = new Date(cur.getTime() + passo);
      else { const p = partesBR(cur); cur = new Date(Date.UTC(p.m === 12 ? p.a + 1 : p.a, p.m % 12, 1, 12)); }
    }
    const kf = chaveDe(fim);
    if (!buckets.has(kf)) buckets.set(kf, { chave: kf, rotulo: rotuloDe(kf), entradas: 0, concluidos: 0, valor: 0 });
  }
  const bucket = (d: Date) => {
    const k = chaveDe(d);
    if (!buckets.has(k)) buckets.set(k, { chave: k, rotulo: rotuloDe(k), entradas: 0, concluidos: 0, valor: 0 });
    return buckets.get(k)!;
  };
  criados.forEach(c => { const b = bucket(criadoEm(c)!); b.entradas++; if (tipoDe(c) !== 'negativa') b.valor += lerV(c); });
  concluidos.forEach(c => { bucket(finais.get(c.id)!).concluidos++; });
  let evolucao = Array.from(buckets.values()).sort((a, b) => a.chave.localeCompare(b.chave));
  // sem os meses vazios do começo (ex.: "Este ano" num fluxo criado em abril)
  while (evolucao.length > 1 && !evolucao[0].entradas && !evolucao[0].concluidos) evolucao = evolucao.slice(1);
  evolucao = evolucao.slice(-18);

  // dimensões (sobre os criados no período)
  const dimsOut = dims.map(dim => {
    const m = new Map<string, { qtd: number; conc: number; ab: number; val: number; ciclos: number[] }>();
    criados.forEach(c => {
      const vals = dim.valores(c);
      const lista = vals.length ? vals : ['(não informado)'];
      const v = tipoDe(c) === 'negativa' ? 0 : lerV(c);
      lista.forEach(x => {
        if (!m.has(x)) m.set(x, { qtd: 0, conc: 0, ab: 0, val: 0, ciclos: [] });
        const r = m.get(x)!;
        r.qtd++;
        r.val += dim.multi ? v / lista.length : v;
        if (tipoDe(c) === 'concluida') {
          r.conc++;
          const a = criadoEm(c), b = finais.get(c.id);
          if (a && b) r.ciclos.push(Math.max(0, (b.getTime() - a.getTime()) / DIA));
        } else if (tipoDe(c) === 'andamento') r.ab++;
      });
    });
    // fornecedor/solicitante escrito à mão: junta maiúscula/minúscula
    let linhas: LinhaDim[] = Array.from(m.entries()).map(([valor, r]) => ({
      valor, qtd: r.qtd, concluidos: r.conc, abertos: r.ab, valorTotal: r.val, cicloMedioDias: media(r.ciclos),
    }));
    if (dim.papel === 'fornecedor' || dim.papel === 'solicitante') {
      const g = new Map<string, LinhaDim & { _c: number }>();
      linhas.forEach(l => {
        const k = l.valor.toLocaleUpperCase('pt-BR').normalize('NFD').replace(/[̀-ͯ]/g, '');
        const atual = g.get(k);
        if (!atual) { g.set(k, { ...l, _c: l.qtd }); return; }
        const nc = (atual.cicloMedioDias ?? 0) * atual.concluidos + (l.cicloMedioDias ?? 0) * l.concluidos;
        const conc = atual.concluidos + l.concluidos;
        const nome = l.qtd > atual._c ? l.valor : atual.valor;
        g.set(k, { valor: nome, qtd: atual.qtd + l.qtd, concluidos: conc, abertos: atual.abertos + l.abertos,
          valorTotal: atual.valorTotal + l.valorTotal, cicloMedioDias: conc ? nc / conc : null, _c: Math.max(atual._c, l.qtd) });
      });
      linhas = Array.from(g.values()).map(({ _c, ...l }) => l);
    }
    linhas.sort((a, b) => (valor ? b.valorTotal - a.valorTotal : 0) || b.qtd - a.qtd);
    return { dim, linhas };
  }).filter(x => x.linhas.some(l => l.valor !== '(não informado)'));

  // aging dos abertos
  const faixas: [string, number][] = [['até 7 dias', 7], ['8 a 15', 15], ['16 a 30', 30], ['31 a 60', 60], ['61 a 90', 90], ['mais de 90', Infinity]];
  const aging = faixas.map(([faixa]) => ({ faixa, qtd: 0 }));
  abertos.forEach(c => {
    const a = criadoEm(c); if (!a) return;
    const d = (agora.getTime() - a.getTime()) / DIA;
    const i = faixas.findIndex(([, lim]) => d <= lim);
    aging[i].qtd++;
  });

  const dimDestaque = dims.find(d => d.papel === 'fornecedor') || dims.find(d => d.papel === 'filial') || dims.find(d => d.papel === 'solicitante');
  const destaque = (c: CardDef): CardDestaque => {
    const a = criadoEm(c);
    return {
      id: c.id, shortCode: c.shortCode, titulo: textoLimpo(String(c.titulo || '')) || '(sem título)',
      etapa: etapaPorId.get(c.etapaId)?.nome || '', valor: lerV(c),
      dias: a ? Math.floor((agora.getTime() - a.getTime()) / DIA) : 0,
      extra: dimDestaque ? dimDestaque.valores(c).join(', ') : '',
    };
  };
  const maioresValores = valor ? criados.filter(c => tipoDe(c) !== 'negativa' && lerV(c) > 0)
    .sort((a, b) => lerV(b) - lerV(a)).slice(0, 8).map(destaque) : [];
  const maisAntigos = [...abertos].filter(c => criadoEm(c))
    .sort((a, b) => criadoEm(a)!.getTime() - criadoEm(b)!.getTime()).slice(0, 8).map(destaque);

  const valorEntradas = criados.filter(c => tipoDe(c) !== 'negativa').reduce((s, c) => s + lerV(c), 0);
  const cardsComValor = criados.filter(c => tipoDe(c) !== 'negativa' && lerV(c) > 0).length;

  // ---- evolução do SLA por etapa: ciclos de 30 dias (período "90 dias") ou mês a mês ----
  const janelas: { rotulo: string; ini: Date; fim: Date }[] = [];
  let slaMesParcial: string | null = null;
  {
    const iniJ = cfg.inicio || (datasCriacao.length ? new Date(Math.min(...datasCriacao.map(d => d.getTime()))) : agora);
    const fimJ = cfg.fim && cfg.fim < agora ? cfg.fim : agora;
    if (cfg.periodoId === '90') {
      for (let k = 0; k < 3; k++) {
        const a = new Date(iniJ.getTime() + k * 30 * DIA);
        const b = k === 2 ? fimJ : new Date(iniJ.getTime() + (k + 1) * 30 * DIA);
        janelas.push({ rotulo: `${fmtD(a).slice(0, 5)} a ${fmtD(new Date(b.getTime() - 1)).slice(0, 5)}`, ini: a, fim: b });
      }
    } else {
      let p = partesBR(iniJ);
      let guard = 0;
      while (guard++ < 60) {
        const a = new Date(Date.UTC(p.a, p.m - 1, 1, 3));
        const b = new Date(Date.UTC(p.m === 12 ? p.a + 1 : p.a, p.m % 12, 1, 3));
        if (a >= fimJ) break;
        janelas.push({ rotulo: `${MESES[p.m - 1]}/${String(p.a).slice(2)}`, ini: a < iniJ ? iniJ : a, fim: b > fimJ ? fimJ : b });
        p = { ...p, a: p.m === 12 ? p.a + 1 : p.a, m: p.m % 12 + 1 };
      }
      // mês corrente com menos de 15 dias fica fora das colunas (poucas passagens, e as abertas ainda curtas)
      const ult = janelas[janelas.length - 1];
      if (janelas.length > 2 && ult && (ult.fim.getTime() - ult.ini.getTime()) / DIA < 15) slaMesParcial = janelas.pop()!.rotulo;
    }
  }
  const agregar = (vs: { horas: number; slaOk: boolean | null }[]): CelulaSla => {
    const t = vs.map(v => v.horas);
    const comSla = vs.filter(v => v.slaOk !== null);
    return { n: vs.length, medioH: media(t), medianoH: mediana(t), pctSla: comSla.length ? (comSla.filter(v => v.slaOk).length / comSla.length) * 100 : null };
  };
  const slaMensal: LinhaSlaMensal[] = fluxo.etapas
    .filter(e => (tipos[e.id] || 'andamento') === 'andamento')
    .map(e => {
      const vs = visitas.filter(v => v.etapaId === e.id);
      return { id: e.id, nome: e.nome, slaH: e.tempoMaximoHoras || null, total: agregar(vs),
        meses: janelas.map(j => agregar(vs.filter(v => v.ent >= j.ini && v.ent < j.fim))) };
    })
    .filter(l => l.total.n >= 5);
  // fluxo inteiro: ciclo e % concluído sem nenhuma passagem fora do SLA (todas as passagens do card)
  const foraDoSla = (c: CardDef): boolean => {
    const h = c.etapaHistory || [];
    return h.some((p, i) => {
      const e = etapaPorId.get(String(p.etapaId));
      if (!e?.tempoMaximoHoras || (tipos[e.id] || 'andamento') !== 'andamento') return false;
      const a = lerData(p.movedAt);
      const b = lerData(p.leftAt) || (i + 1 < h.length ? lerData(h[i + 1].movedAt) : null);
      return !!(a && b && b >= a && horas(a, b, e.slaIgnorarFimDeSemana) > e.tempoMaximoHoras);
    });
  };
  const fluxoMensal = janelas.map(j => {
    const cs = concluidos.filter(c => { const d = finais.get(c.id); return !!d && d >= j.ini && d < j.fim; });
    const cic = cs.map(c => ciclo.get(c.id)).filter((x): x is number => x !== undefined);
    return { concluidos: cs.length, cicloMedioDias: media(cic), pctNoPrazo: cs.length ? (cs.filter(c => !foraDoSla(c)).length / cs.length) * 100 : null };
  });
  const fluxoTotal = {
    concluidos: concluidos.length, cicloMedioDias: media(ciclos),
    pctNoPrazo: concluidos.length ? (concluidos.filter(c => !foraDoSla(c)).length / concluidos.length) * 100 : null,
  };

  const periodoTexto = cfg.inicio || cfg.fim
    ? `${cfg.inicio ? fmtD(cfg.inicio) : 'início'} a ${cfg.fim ? fmtD(new Date(cfg.fim.getTime() - 1)) : fmtD(agora)}`
    : `todo o histórico (até ${fmtD(agora)})`;

  return {
    nomeFluxo: fluxo.name || fluxo.nome || 'Fluxo',
    totalFluxo: cards.length,
    entradas: criados.length,
    concluidos: concluidos.length,
    reprovados: reprovados.length,
    abertosHoje: abertos.length,
    atrasadosHoje: atrasado.size,
    comSlaHoje,
    cicloMedioDias: media(ciclos),
    cicloMedianoDias: mediana(ciclos),
    semDataConclusao,
    valorNome: valor?.nome || null,
    valorEntradas,
    valorConcluido: concluidos.reduce((s, c) => s + lerV(c), 0),
    ticketMedio: cardsComValor ? valorEntradas / cardsComValor : null,
    cardsComValor,
    etapas, evolucao, granularidade, dims: dimsOut, aging, maioresValores, maisAntigos, periodoTexto,
    slaJanelas: janelas.map(j => j.rotulo), slaCiclos: cfg.periodoId === '90', slaMesParcial, slaMensal, fluxoMensal, fluxoTotal,
  };
}

// ---------- períodos prontos ----------

export type PeriodoId = 'mes' | 'mes_passado' | '30' | '90' | 'trimestre' | 'ano' | 'tudo' | 'custom';
export const PERIODOS: { id: PeriodoId; label: string }[] = [
  { id: 'mes', label: 'Este mês' }, { id: 'mes_passado', label: 'Mês passado' }, { id: '30', label: '30 dias' },
  { id: '90', label: '90 dias' }, { id: 'ano', label: 'Este ano' }, { id: 'tudo', label: 'Tudo' }, { id: 'custom', label: 'Escolher datas' },
];

/** Início/fim (fim exclusivo) em hora de Brasília (UTC-3, sem horário de verão). */
export function intervaloDe(id: PeriodoId, agora: Date, de?: string, ate?: string): { inicio: Date | null; fim: Date | null } {
  const br = (a: number, m: number, d: number) => new Date(Date.UTC(a, m, d, 3)); // 00:00 em Brasília
  const p = partesBR(agora);
  switch (id) {
    case 'mes': return { inicio: br(p.a, p.m - 1, 1), fim: null };
    case 'mes_passado': return { inicio: br(p.a, p.m - 2, 1), fim: br(p.a, p.m - 1, 1) };
    case '30': return { inicio: br(p.a, p.m - 1, p.dia - 29), fim: null };
    case '90': return { inicio: br(p.a, p.m - 1, p.dia - 89), fim: null };
    case 'ano': return { inicio: br(p.a, 0, 1), fim: null };
    case 'custom': {
      const f = (s?: string, mais = 0) => s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? br(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)) + mais) : null;
      return { inicio: f(de), fim: f(ate, 1) };
    }
    default: return { inicio: null, fim: null };
  }
}
